import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  FormHelperText,
} from "@mui/material";
import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { LockReset, Visibility, VisibilityOff } from "@mui/icons-material";
import CustomSnackbar from "components/CustomSnackbar";
import DotPulseLoading from "components/Loading/DotPulseLoading";
import { useTranslation } from "react-i18next";
import { getmessage } from "utils/i18messages";

const PasswordForm = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current: "",
    new: "",
    confirm: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const handleChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleClickShowPassword = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!passwordData.current || !passwordData.new || !passwordData.confirm) {
      setSnackbar({
        open: true,
        message: getmessage("profile_settings.password_required"),
        severity: "error",
      });
      return;
    }

    if (passwordData.new !== passwordData.confirm) {
      setSnackbar({
        open: true,
        message: getmessage("profile_settings.password_mismatch"),
        severity: "error",
      });
      return;
    }

    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;

    if (!passwordRegex.test(passwordData.new)) {
      setSnackbar({
        open: true,
        message: getmessage("profile_settings.password_invalid"),
        severity: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const lang = localStorage.getItem("i18nextLng") || "en";

      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/auth/change-password`,
        {
          current: passwordData.current,
          newPassword: passwordData.new,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept-Language": lang,
          },
        }
      );

      setSnackbar({
        open: true,
        message: response.data.message || "Password updated successfully.",
        severity: "success",
      });
      setPasswordData({ current: "", new: "", confirm: "" });
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Password update failed.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Password strength indicator
  const passwordStrength = () => {
    if (!passwordData.new) return null;

    const hasMinLength = passwordData.new.length >= 8;
    const hasUpperCase = /[A-Z]/.test(passwordData.new);
    const hasLowerCase = /[a-z]/.test(passwordData.new);
    const hasNumber = /\d/.test(passwordData.new);
    const hasSpecialChar = /[@$!%*?&#]/.test(passwordData.new);

    return (
      <Box sx={{ mt: 1 }}>
        <FormHelperText>
          {t("Dashboard_profile.password.requirements.title")}
        </FormHelperText>
        <FormHelperText
          sx={{ color: hasMinLength ? "success.main" : "error.main" }}
        >
          {t("Dashboard_profile.password.requirements.minLength")}
        </FormHelperText>
        <FormHelperText
          sx={{ color: hasUpperCase ? "success.main" : "error.main" }}
        >
          {t("Dashboard_profile.password.requirements.uppercase")}
        </FormHelperText>
        <FormHelperText
          sx={{ color: hasLowerCase ? "success.main" : "error.main" }}
        >
          {t("Dashboard_profile.password.requirements.lowercase")}
        </FormHelperText>
        <FormHelperText
          sx={{ color: hasNumber ? "success.main" : "error.main" }}
        >
          {t("Dashboard_profile.password.requirements.number")}
        </FormHelperText>
        <FormHelperText
          sx={{ color: hasSpecialChar ? "success.main" : "error.main" }}
        >
          {t("Dashboard_profile.password.requirements.specialChar")}
        </FormHelperText>
      </Box>
    );
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ mb: 2, fontSize: { xs: "1.1rem", sm: "1.25rem" } }}
        >
          {t("Dashboard_profile.password.title")}
        </Typography>

        <TextField
          id="current-password"
          fullWidth
          label={t("Dashboard_profile.password.current")}
          name="current"
          type={showPassword.current ? "text" : "password"}
          value={passwordData.current}
          onChange={handleChange}
          margin="normal"
          size="small"
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={t("Dashboard_profile.password.toggleVisibility")}
                  onClick={() => handleClickShowPassword("current")}
                  edge="end"
                >
                  {showPassword.current ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          id="new-password"
          fullWidth
          label={t("Dashboard_profile.password.new")}
          name="new"
          type={showPassword.new ? "text" : "password"}
          value={passwordData.new}
          onChange={handleChange}
          margin="normal"
          size="small"
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={t("Dashboard_profile.password.toggleVisibility")}
                  onClick={() => handleClickShowPassword("new")}
                  edge="end"
                >
                  {showPassword.new ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {passwordStrength()}

        <TextField
          id="confirm-password"
          fullWidth
          label={t("Dashboard_profile.password.confirm")}
          name="confirm"
          type={showPassword.confirm ? "text" : "password"}
          value={passwordData.confirm}
          onChange={handleChange}
          margin="normal"
          size="small"
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={t("Dashboard_profile.password.toggleVisibility")}
                  onClick={() => handleClickShowPassword("confirm")}
                  edge="end"
                >
                  {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "flex-end",
            "& .MuiButton-root": {
              width: { xs: "100%", sm: "auto" },
            },
          }}
        >
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            startIcon={<LockReset />}
            size="medium"
            disabled={loading}
          >
            {loading
              ? t("Dashboard_profile.password.updating")
              : t("Dashboard_profile.password.update")}
          </Button>
        </Box>
      </form>

      {/* Modern Snackbar for notifications */}
      <CustomSnackbar
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
      />

      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DotPulseLoading size={14} color="#3f51b5" />
        </Box>
      )}
    </>
  );
};

export default PasswordForm;
